import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HistoryIcon from "@mui/icons-material/History";
import useFetch from "../../../../hooks/useFetch";
import {
    PageHeader,
    EmptyState,
    Skeleton,
} from "../../../shares/dashboard/DashboardWidgets";
import CurrentExam from "./cards/CurrentExam";
import PreviousExam from "./cards/PreviousExam";

/**
 * Exams.
 *
 * Splitting live from finished exams was previously done in a useEffect that
 * wrote two extra state variables on every change of examInfo - three renders
 * for one fetch. useMemo derives both lists instead, with the same predicates.
 */
function Index() {
    const [examInfo, setExamInfo] = useState();

    const fetchData = useFetch({
        method: "GET",
    safeRead: true,
        url: `exam-platform/`,
        noHeader: false,
        trigger: true,
        setter: setExamInfo,
        errMessage: () =>
            toast.error("مشکلی در دریافت اطلاعات پیش آمده", {
                position: "bottom-left",
                rtl: true,
                autoClose: 2000,
            }),
    });

    const { current, previous } = useMemo(() => {
        const list = Array.isArray(examInfo) ? examInfo : [];
        return {
            current: list.filter(
                (exam) =>
                    exam.exam_permission === true &&
                    !exam.score.exam_finished &&
                    exam.ExamFinished === false
            ),
            previous: list.filter(
                (exam) =>
                    exam.exam_permission === false ||
                    exam.score.exam_finished ||
                    exam.ExamFinished === true
            ),
        };
    }, [examInfo]);

    const isLoading = fetchData.loading && !examInfo;

    return (
        <div className="flex flex-col gap-2xl pb-4xl">
            <PageHeader
                title="امتحانات"
                description="آزمون‌های در دسترس و کارنامه‌های قبلی"
                icon={TextSnippetIcon}
            />

            <section className="flex flex-col gap-md">
                <h2 className="text-md font-semibold text-ink">آزمون‌های باز</h2>
                {isLoading ? (
                    <Skeleton className="h-[16rem] w-full" />
                ) : current.length === 0 ? (
                    <EmptyState
                        icon={TaskAltIcon}
                        title="آزمون بازی نداری"
                        description="هر وقت آزمون جدیدی فعال بشه، همین‌جا نشونت می‌دیم."
                    />
                ) : (
                    <div className="flex flex-col gap-md">
                        {current.map((exam, index) => (
                            <CurrentExam
                                key={exam.score.exam}
                                exam={exam}
                                index={index}
                            />
                        ))}
                    </div>
                )}
            </section>

            <section className="flex flex-col gap-md">
                <h2 className="text-md font-semibold text-ink">امتحانات قبلی</h2>
                {isLoading ? (
                    <div className="flex flex-col gap-md">
                        {[0, 1].map((key) => (
                            <Skeleton key={key} className="h-[14rem] w-full" />
                        ))}
                    </div>
                ) : previous.length === 0 ? (
                    <EmptyState
                        icon={HistoryIcon}
                        title="هنوز آزمونی نداده‌ای"
                        description="نتیجه آزمون‌هایی که شرکت می‌کنی اینجا بایگانی می‌شود."
                    />
                ) : (
                    <div className="flex flex-col gap-md">
                        {previous.map((exam, index) => (
                            <PreviousExam
                                key={exam.score.exam}
                                exam={exam}
                                index={index}
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}

export default Index;
